import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { createUseFetchSearchWithCustomPropsFn } from '@common/search.services';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosErrorResponse } from '@core/error.models';
import { IItemResponse, IPagedResultsV1 } from '@gcl/react-core';
import {
  IProject,
  IProjectInterested,
  IProjectSearchFilter,
  IProjectSearchFilterResponse,
  IUpdateProjectRequest,
} from './project.models';
import {
  getProjectByProjectNoAsync,
  getProjectDataAllAsync,
  putProjectArchiveAsync,
  putProjectAsync,
  searchProjectAsync,
  putProjectUnarchiveAsync,
  postProjectInterestedAsync,
  deleteProjectInterestedAsync,
} from './project.services';

export const useGetProjectDataAllAsync = (enabled = true) => {
  return useQuery<IPagedResultsV1<IProject>, AxiosErrorResponse>(
    [FETCH_QUERIES_NAME.PROJECT_DATA_ALL],
    async () => {
      const result = await getProjectDataAllAsync();
      return result;
    },
    {
      enabled,
    },
  );
};

export const useGetProjectByProjectNoAsync = (projectNo: string | undefined, enabled = true) => {
  return useQuery<IItemResponse<IProject>, AxiosErrorResponse>(
    [FETCH_QUERIES_NAME.PROJECT_DATA_ALL],
    async () => {
      const result = await getProjectByProjectNoAsync(projectNo);
      return result;
    },
    {
      enabled: enabled && !!projectNo,
    },
  );
};

export const useFetchProjectSearch = createUseFetchSearchWithCustomPropsFn<
  IProjectSearchFilter,
  IProjectSearchFilterResponse
>({
  queryName: FETCH_QUERIES_NAME.PROJECT_SEARCH,
  searchAsyncFn: searchProjectAsync,
});

export const useUpdateProject = () => {
  return useMutation<IItemResponse<IProject>, AxiosErrorResponse, IUpdateProjectRequest>(
    [MUTATION_KEYS.UPDATE_PROJECT_INFO],
    async (request: IUpdateProjectRequest) => {
      const result = await putProjectAsync(request);
      return result;
    },
  );
};

export const useArchiveProject = () => {
  return useMutation<IItemResponse<IProject>, AxiosErrorResponse, number>(
    [MUTATION_KEYS.UPDATE_PROJECT_INFO],
    async (id: number) => {
      const result = await putProjectArchiveAsync(id);
      return result;
    },
  );
};

export const useUnarchiveProject = () => {
  return useMutation<IItemResponse<IProject>, AxiosErrorResponse, number>(
    [MUTATION_KEYS.UPDATE_PROJECT_INFO],
    async (id: number) => {
      const result = await putProjectUnarchiveAsync(id);
      return result;
    },
  );
};

export const useAddProjectInterested = () => {
  return useMutation<IItemResponse<IProjectInterested>, AxiosErrorResponse, number>(
    [MUTATION_KEYS.ADD_PROJECT_INTERESTED],
    async (projectId: number) => {
      const result = await postProjectInterestedAsync(projectId);
      return result;
    },
  );
};
export const useDeleteProjectInterested = () => {
  return useMutation<boolean, AxiosErrorResponse, number>(
    [MUTATION_KEYS.DELETE_PROJECT_INTERESTED],
    async (projectId: number) => {
      const result = await deleteProjectInterestedAsync(projectId);
      return result;
    },
  );
};
